import React, { useState, useEffect } from 'react';
import styles from './WFSIntegrationToggle.module.css';
import { useUserContext } from '@/contexts/UserContext';
import { useErrorModalContext } from '@/contexts/ErrorModalContext';
import { disconnectWFS, getWFSAuthLink, handleWFSCallback } from '@/api/wfs/auth';
import { useSearchParams } from 'wouter';
import WalmartLogo from './walmart.logo';
import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';

const WFSIntegrationToggle = () => {
    const { user, setUser } = useUserContext();
    const [isActive, setIsActive] = useState(user?.wfsSellerId || false);
    const [isLoading, setIsLoading] = useState(false);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const { setErrors } = useErrorModalContext();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        setIsActive(user?.wfsSellerId);
    }, [user]);

    // Handle OAuth callback
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const sellerId = searchParams.get('sellerId');
        const type = searchParams.get('type');
        const partnerType = searchParams.get('partnerType');
        console.log('code', code);
        console.log('state', state);
        console.log('sellerId', sellerId);
        console.log('type', type);
        console.log('partnerType', partnerType);

        if (code && state && sellerId && type && partnerType) {
            handleCallback(code, state, sellerId, type, partnerType);
        }
    }, [searchParams]);

    const handleToggle = async () => {
        if (isLoading) return;
        
        try {
            if (!isActive) {
                setIsLoading(true);
                // Persist OAuth state across page reloads
                const { url: oauthUrl } = await getWFSAuthLink();
                window.location.href = oauthUrl;
            }
            else {
                // Show confirmation modal instead of disconnecting immediately
                setDisconnectModalOpen(true);
            }

        } catch (error) {
            console.error('Error connect/disconnect to WFS:', error);
            setErrors([{ 
                message: `Failed to ${user?.wfsSellerId ? 'disconnect' : 'connect'} to WFS. Please try again later.`
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            setIsLoading(true);
            await disconnectWFS();
            setIsActive(false);
            setUser({ ...user, wfsSellerId: null }, false);
        } catch (error) {
            console.error('Error disconnecting from WFS:', error);
            setErrors([{ 
                message: "Failed to disconnect from WFS. Please try again later."
            }]);
        } finally {
            setIsLoading(false);
            setDisconnectModalOpen(false);
        }
    };

    const handleCallback = async (code: string, state: string, sellerId: string, type: string, partnerType: string) => {
        try {
            setIsLoading(true);
            await handleWFSCallback(code, state, sellerId, type, partnerType);
            setIsActive(true);
            setUser({ ...user, wfsSellerId: sellerId }, false);
            
            // remove the wfs oauth query params from the browser url and reload the page
            // so user object is updated
            window.location.href = window.location.pathname;
        } catch (error) {
            console.error('Error completing WFS connection:', error);
            setErrors([{ 
                message: "Failed to complete WFS connection. Please try again later."
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleWalmartClick = () => {
        // Open Walmart Seller Central in a new tab
        window.open('https://seller.walmart.com/', '_blank');
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.logoSection}>
                    <div className={styles.logoContainer}>
                        <WalmartLogo />
                    </div>
                    <div className={`${styles.statusBadge} ${isActive ? styles.statusActive : ''}`}>
                        {isActive ? 'Active' : 'Not Active'}
                    </div>
                </div>

                <div className={styles.contentSection}>
                    <div className={styles.consentText}>
                        Full support for Walmart Marketplace, manage and scale your WFS operations seamlessly.
                    </div>
                </div>

                <div className={styles.toggleSection}>
                    <button 
                        onClick={handleToggle}
                        disabled={isLoading}
                        className={styles.primaryButton}
                        aria-label={isActive ? 'Disconnect WFS integration' : 'Connect WFS integration'}
                        aria-busy={isLoading}
                    >
                        {isActive ? 'Disconnect' : 'Connect'}
                    </button>

                    {isActive && (
                        <button 
                            onClick={handleWalmartClick}
                            className={styles.walmartButton}
                            aria-label="Open Walmart Seller Central"
                        >
                            Walmart →
                        </button>
                    )}
                </div>
            </div>

            <ConfirmModal
                open={disconnectModalOpen}
                loading={isLoading}
                onClose={(confirmed: boolean) => {
                    if (confirmed) {
                        handleDisconnect();
                    } else {
                        setDisconnectModalOpen(false);
                    }
                }}
                title="Disconnect WFS Integration"
                body={
                    <p className={styles.modalBodyText}>
                        Disconnecting your WFS integration will remove access to all Walmart Fulfillment Services data in your account. You will no longer be able to manage WFS shipments or inventory through Boxem. Are you sure you wish to continue?
                    </p>
                }
                confirmButtonLabel="Disconnect WFS Integration"
                confirmButtonSentinment="danger"
            />
        </>
    );
};

export default WFSIntegrationToggle;
